import React, { useEffect, useMemo, useState } from 'react';

import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useQuery } from '../../hooks/useQuery';

import { Button, Col, Empty, Layout, Row } from 'antd';
import ButtonAdd from '../../components/ButtonAdd/ButtonAdd';
import ModalCreateStage from './ModalCreateStage';

import { ReactComponent as Filter } from '../../assets/filter-icon.svg';
import { ReactComponent as Pluse } from './img/pluse-icon.svg';
import FinplanStegeItem from './FinplanStegeItem';
import ModalCreateItem from './ModalCreateItem/ModalCreateItem';
import ItemsListItem from './ItemsListItem';
import { Bubble } from '../../components/charts/Bubble/Bubble';
import { numberWithSpacesInter } from '../../helpers/formatInteger';
import ModalFilterItems from './ModalFilterItems';

const { Header } = Layout;

const FinancialPlanStagesSummary = ({
  t,
  financialPlan,
  userId,
  finplanId,
  openCreateStageModal,
  setOpenCreateStageModal,
}) => {
  const history = useHistory();
  const { i18n } = useTranslation('');
  const { stageId } = useParams();
  const { search, pathname } = useLocation();
  const query = useQuery(search);

  const category = useSelector(
    (state) => state.accountReducer.financialCategory,
  );

  const initValue = {
    happiness: ['ALL'],
    purpose: ['ALL'],
    necessity: ['ALL'],
    probability: ['ALL'],
  };

  const itemFromQuery = query.get('item');
  const itemTypeFromQuery = query.get('item-type');
  const globalCategory = query.get('global-category');

  const [openModalCreateItem, setOpenModalCreateItem] = useState(null);
  const [selectedPlanItems, setSelectedPlanItems] = useState(null);
  const [openFilterModal, setOpenFilterModal] = useState(false);
  const [filterItems, setFilterItems] = useState(initValue);

  useEffect(() => {
    if (itemFromQuery) {
      setSelectedPlanItems(itemFromQuery);
    } else {
      setSelectedPlanItems(null);
    }
  }, [itemFromQuery]);

  const closeModalItem = () => {
    setOpenModalCreateItem(null);
  };

  const handleItemType = (type) => {
    const params = new URLSearchParams(search);
    params.set('item-type', type);

    const availableCategories = category?.[type]?.map((cat) => cat.id) || [];

    if (availableCategories.length > 0) {
      params.set('global-category', availableCategories.join('+'));
    } else {
      params.delete('global-category');
    }

    history.push({
      pathname,
      search: `?${params.toString()}`,
    });
  };

  const handleCreateItem = (type) => {
    setOpenModalCreateItem(type);
  };

  const currentItemType = query.get('item-type');
  const globalCategoryParam = query.get('global-category');
  const globalCategories = globalCategoryParam
    ? globalCategoryParam.split('+')
    : [];
  const viewType = query.get('view-type');

  useEffect(() => {
    if (!viewType) {
      const params = new URLSearchParams(search);
      params.set('view-type', 'table');
      history.push({
        pathname,
        search: `?${params.toString()}`,
      });
    }
  }, [currentItemType]);

  const language = i18n.languages?.length ? i18n.languages[0] : 'en';
  const currentLang = ['en', 'sw'].includes(language) ? language : 'en';

  useEffect(() => {
    if (stageId && !currentItemType) {
      history.replace(`${pathname}?item-type=expenses`);
    }
  }, [stageId]);

  const getStageById = () => {
    if (financialPlan?.stages) {
      const stage =
        financialPlan.stages.find((item) => item.id === +stageId) ?? {};
      return stage;
    }
    return {};
  };

  const getStageTypeTotal = (type) => {
    const stage = getStageById();
    const items = stage?.[type] ?? [];
    if (itemTypeFromQuery === type) {
      const selectedGlobalCategory = globalCategory?.split('+') || [];

      return items
        .filter((i) => selectedGlobalCategory.includes(i.global_category))
        .reduce((sum, item) => sum + item?.avg_amount_yearly_sek, 0);
    } else {
      return items.reduce((sum, item) => sum + item?.avg_amount_yearly_sek, 0);
    }
  };

  const closeCreateModal = () => {
    setOpenCreateStageModal(null);
  };

  const handeleFilter = () => {
    setOpenFilterModal(true);
  };

  const handleToggleMode = () => {
    const params = new URLSearchParams(search);
    params.set('view-type', viewType === 'table' ? 'bubble' : 'table');
    history.push({
      pathname,
      search: `?${params.toString()}`,
    });
  };

  const goToStage = () => {
    history.push(`/accounts/${userId}/lifetiming/${finplanId}/stages`);
  };

  const onBubbleDoubleClick = (itemId) => {
    const params = new URLSearchParams(search);
    params.set('item', itemId);
    params.set('view-type', 'table');
    history.push({
      pathname,
      search: `?${params.toString()}`,
    });
  };

  const currentStageItems = useMemo(() => {
    const items = getStageById()
      ?.[currentItemType]?.slice()
      ?.filter(
        (i) =>
          globalCategories.length > 0 &&
          globalCategories.includes(i.global_category),
      );
    let filteredItems = items;
    if (currentItemType === 'expenses') {
      filteredItems = items.filter((item) => {
        const valid =
          (filterItems.happiness[0] === 'ALL' ||
            filterItems.happiness.includes(item.happiness)) &&
          (filterItems.purpose[0] === 'ALL' ||
            filterItems.purpose.includes(item.purpose)) &&
          (filterItems.necessity[0] === 'ALL' ||
            filterItems.necessity.includes(item.necessity));
        return valid;
      });
    } else {
      filteredItems = items.filter((item) => {
        return filterItems.probability[0] === 'ALL'
          ? true
          : filterItems.probability.includes(item.probability);
      });
    }
    return filteredItems;
  }, [
    getStageById(),
    currentItemType,
    globalCategories,
    filterItems,
    financialPlan,
  ]);

  return (
    <div className="finplan" style={{ position: 'relative' }}>
      <Header className="headerStyle">
        <Row className="overview-tabs-wrapper">
          <Col className="tabs-tab-wrapper" span={12}>
            <h1 className="suggested-portfolios-title header-plan">
              {t('FINANCIAL_PLAN')}
            </h1>
            <span className="header-plan-name" onClick={goToStage}>
              {financialPlan?.name}
            </span>
          </Col>
          <Col span={12} className="action-btn-wrapper">
            <ButtonAdd
              title="EDIT_FINANCIAL_PLAN"
              height="32px"
              className="edit-financial-plan"
              onClick={() =>
                history.push(`/accounts/${userId}/lifetiming/${finplanId}`)
              }
            />
            <ButtonAdd
              disabled={financialPlan?.stages?.length >= 5}
              title="ADD_LIFE_STAGE"
              height="32px"
              onClick={() => setOpenCreateStageModal('new')}
            />
          </Col>
        </Row>
      </Header>
      <div className="finplan-stages-container">
        <div className="finplan-stages-label">
          {/* {category?.[currentItemType]?.map(({ id, name }) => {
            return (
              <div key={id} className="finplan-stages-label-item">
                <span className="vertical-bottom-to-top">
                  {t(name[currentLang])}
                </span>
                <div className={`fp-dot-${id}`} />
              </div>
            );
          })} */}
        </div>
        <div className="stages-aside-wrapper">
          {financialPlan?.stages && category && (
            <FinplanStegeItem
              key={stageId}
              stage={getStageById()}
              count={0}
              setOpenCreateStageModal={setOpenCreateStageModal}
              currentLang={currentLang}
              categoryObj={category}
              itemType={currentItemType}
              withoutSelector={true}
            />
          )}
        </div>
        <div className="stages-list-wrapper">
          <div className="stages-list-header">
            <p className="stages-category-name">{getStageById()?.name}</p>
            <div className="stages-category-btn-wrapper">
              <Button
                type="text"
                onClick={() => handleItemType('expenses')}
                className={`stage-category-btn ${currentItemType === 'expenses' ? 'selected' : ''}`}
              >
                <div className="stage-category-dot expenses" />
                <div className="stage-category-type">{t('SPEND')}</div>
                <div className="stage-category-amount">
                  <span>
                    {numberWithSpacesInter(getStageTypeTotal('expenses'), 0)}
                  </span>
                  <span>SEK</span>
                </div>
              </Button>
              <Button
                type="text"
                onClick={() => handleItemType('incomes')}
                className={`stage-category-btn ${currentItemType === 'incomes' ? 'selected' : ''}`}
              >
                <div className="stage-category-dot incomes" />
                <div className="stage-category-type">{t('INCOME')}</div>
                <div className="stage-category-amount">
                  <span>
                    {numberWithSpacesInter(getStageTypeTotal('incomes'), 0)}
                  </span>
                  <span>SEK</span>
                </div>
              </Button>
            </div>
            <div className="stages-category-action-wrapper">
              <Button
                className="filter-btn"
                type="default"
                onClick={handeleFilter}
                icon={<Filter />}
                iconPosition="end"
              >
                {t('FILTER')}
              </Button>
              <div className="view-selector-wrapper" onClick={handleToggleMode}>
                <div
                  className={`view-selector-btn ${viewType === 'table' ? 'list' : 'timeline'}`}
                >
                  {t(viewType === 'table' ? 'LIST' : 'TIMELINE')}
                </div>
                <div className="view-selector-position">{t('LIST')}</div>
                <div className="view-selector-position">{t('TIMELINE')}</div>
              </div>
              <div
                className={`add-item-btn ${globalCategories.length !== 1 ? 'disabled' : ''}`}
                onClick={() => {
                  if (globalCategories.length === 1) {
                    handleCreateItem({
                      currentItemType,
                      globalCategory: globalCategories[0],
                      itemId: 'new',
                    });
                  }
                }}
              >
                <Pluse />
              </div>
            </div>
          </div>
          <div className="stages-list-content">
            {viewType === 'table' ? (
              currentStageItems?.map((item) => {
                return (
                  <ItemsListItem
                    key={item.id}
                    userId={userId}
                    finplanId={finplanId}
                    type={currentItemType}
                    item={item}
                    selected={item.id === selectedPlanItems}
                    setSelectedPlanItems={(id) => {
                      setSelectedPlanItems(id);
                      // Update URL with item query parameter
                      const params = new URLSearchParams(search);
                      if (id) {
                        params.set('item', id);
                      } else {
                        params.delete('item');
                      }
                      history.replace({
                        pathname,
                        search: `?${params.toString()}`,
                      });
                    }}
                    stageId={stageId}
                    setOpenModalCreateItem={setOpenModalCreateItem}
                  />
                );
              })
            ) : currentStageItems?.length ? (
              <Bubble
                data={currentStageItems}
                startYear={getStageById()?.start_year}
                endYear={getStageById()?.end_year}
                grid={false}
                onBubbleDoubleClick={onBubbleDoubleClick}
                itemsType={itemTypeFromQuery}
              />
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </div>
        </div>
      </div>
      <ModalCreateStage
        t={t}
        closeCreateModal={closeCreateModal}
        open={openCreateStageModal}
        planId={+finplanId}
        currentLang={currentLang}
        financialPlan={financialPlan}
        category={category}
      />
      <ModalCreateItem
        open={openModalCreateItem}
        closeModal={closeModalItem}
        financialPlan={financialPlan}
        stageId={stageId}
        globalCategory={openModalCreateItem?.globalCategory}
      />
      <ModalFilterItems
        key={currentItemType}
        type={currentItemType}
        openFilterModal={openFilterModal}
        setOpenFilterModal={setOpenFilterModal}
        filterItems={filterItems}
        setFilterItems={setFilterItems}
      />
    </div>
  );
};

export default FinancialPlanStagesSummary;

FinancialPlanStagesSummary.propTypes = {
  t: PropTypes.func,
  financialPlan: PropTypes.object,
  finplanId: PropTypes.string,
  userId: PropTypes.string,
  openCreateStageModal: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  setOpenCreateStageModal: PropTypes.func,
};
